import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sortPlansByCategory } from '@/helpers';
import { Button, ChecksPriceEntry, DropdownPanel, SvgIcon } from '@/components';

import { Plan } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { paymentActions } from '@/redux/actions';
import { profileSelectors } from '@/redux/selectors';
import {
  MODE_PLANS_AVAILABILITY,
  navigationPages,
  PlanCategoryEnum,
} from '@/config';

import styles from './BalanceInfo.module.sass';

interface BalanceInfoProps {
  isActive: boolean;
  balance: number;
  plans: Plan[] | null;
  isUserLocked: boolean;
  isPromo?: boolean;
  onToggle: (isActive: boolean) => void;
}

const BalanceInfo: FC<BalanceInfoProps> = (props) => {
  const { isActive, balance, plans, isUserLocked, isPromo, onToggle } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [selectedEntry, setSelectedEntry] = useState<Plan | null>(null);
  const [activeCategory, setActiveCategory] = useState<PlanCategoryEnum | null>(
    null,
  );
  const [planCategories, setPlanCategories] = useState<Record<string, Plan[]>>(
    {},
  );
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );

  useEffect(() => {
    dispatch(paymentActions.fetchPriceListPlans());
  }, []);

  useEffect(() => {
    if (profileData?.accountMode) {
      const initialCategory =
        MODE_PLANS_AVAILABILITY[profileData.accountMode].available[0];
      setActiveCategory(initialCategory);
    }
  }, [profileData?.accountMode]);

  useEffect(() => {
    if (plans) {
      const sortedCategories = sortPlansByCategory(plans);
      setPlanCategories(() => sortedCategories);
    }
  }, [plans]);

  const hasPlansInCategory = !!(
    activeCategory && planCategories[activeCategory]?.length
  );

  const handleOpenDropdown = useCallback(() => {
    if (isUserLocked) return;
    if (!hasPlansInCategory) return;

    onToggle(true);
  }, [isUserLocked, hasPlansInCategory, onToggle]);

  const goToTariffs = () => {
    onToggle(false);
    navigate(navigationPages.TARIFFS.path, {
      state: { planId: selectedEntry?.id },
    });
  };

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        onClick={handleOpenDropdown}
        disabled={!hasPlansInCategory}
        className={styles.button}
      >
        <SvgIcon name="aml" />
        <span className={styles.text}>{balance}</span>

        {isPromo && (
          <span className={styles.free}>
            | {t('translation.btn_free_check')}
          </span>
        )}
      </button>

      <DropdownPanel
        isActive={isActive}
        onClose={() => onToggle(false)}
        className={styles.panel}
      >
        <div className={styles.title}>
          {t('translation.buy_checks')}
          {/* {activeCategory ? tariffDisplayData[activeCategory].title : ''} */}
        </div>

        {planCategories && activeCategory && (
          <div className={styles.list}>
            {planCategories[activeCategory].map((entry) => {
              return (
                <ChecksPriceEntry
                  key={entry.id}
                  isActive={entry === selectedEntry}
                  item={entry}
                  onClick={() => setSelectedEntry(entry)}
                />
              );
            })}
          </div>
        )}

        <Button
          size="m"
          disabled={!selectedEntry}
          onClick={goToTariffs}
          fullwidth
        >
          {t('translation.buy_checks')}
        </Button>

        {/* <div className={styles.discover}>
          <Link to={navigationPages.TARIFFS.path}>
            <span>{t('translation.discover_more_advantageous_plans')}</span>
            <SvgIcon name="arrow-right" />
          </Link>
        </div> */}

        <button
          type="button"
          className={styles.close}
          onClick={() => onToggle(false)}
        >
          <SvgIcon name="close" />
        </button>
      </DropdownPanel>
    </div>
  );
};

export default BalanceInfo;
